import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  type BidCsvImportIssuerCount,
  type BidCsvImportNotice,
  hasBidCsvImportExcluded,
} from '~/models/bidCsvImportReturn'

/**
 * 入札 CSV の取込から入札画面へ移ってきた時に、 全部取り込めたなら成功のトースト
 * 「{n}件を取り込みました」 を 1 回だけ出す (AC-SPOT-024・AC-BCI-038)。
 * 対象外の行があった時はトーストに代えてエラーバナーにするので、 ここでは出さない。
 *
 * MainLayout は SideMenuLayout の中の画面をモバイル用・デスクトップ用に二重に描画するので、 この hook も
 * 2 つの instance で走る。 `shownRef` は instance ごとなので、 取込 1 回 (= 同じ history entry) の
 * toast は `location.key` を id にして 1 件にまとめる。
 *
 * `otherIssuers` は、 入札一覧で選択中の発注主体区分とは違う区分に入った札の区分別の件数
 * (AC-SPOT-024。 選択中と違う区分の札は一覧に出ないので、 区分名と件数を文に入れて知らせる)。
 * - 空: 入札一覧を発注主体区分で絞らない画面 (時間前)、 または全部が選択中の区分に入った時
 * - undefined: 選択中の区分がまだ決まらない間。 決まる (空か値が渡る) まで出さない
 */
export function useBidCsvImportSuccessToast(
  notice: BidCsvImportNotice | undefined,
  otherIssuers: readonly BidCsvImportIssuerCount[] | undefined,
): void {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { key: locationKey } = useLocation()
  const shownRef = useRef(false)

  useEffect(() => {
    if (notice === undefined || otherIssuers === undefined) return
    if (shownRef.current) return
    shownRef.current = true
    if (hasBidCsvImportExcluded(notice)) return
    const issuers = otherIssuers.map(({ issuerType, count }) =>
      t('page:biddingCommon.csvImportDone.issuerItem', {
        issuer: issuerTypeLabel(issuerType),
        count,
      }),
    )
    showSuccessToast({
      id: `bid-csv-import-done:${locationKey}`,
      title:
        issuers.length === 0
          ? t('page:biddingCommon.csvImportDone.title', {
              count: notice.importedCount,
            })
          : t('page:biddingCommon.csvImportDone.titleWithOtherIssuers', {
              count: notice.importedCount,
              issuers: issuers.join(
                t('page:biddingCommon.csvImportDone.issuerSeparator'),
              ),
            }),
    })
  }, [locationKey, notice, otherIssuers, showSuccessToast, t])
}
